import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Loader2 } from 'lucide-react'
import { useGetTokenPrice } from '@/hooks/use-getTokenPrice'
import {
  SECH_TOKEN_DECIMAL,
  SELL_TOKEN_ABBR,
  SWAP_TOKEN_DECIMALS
} from '@/constants'
import { IS_TESTNET } from '@/constants/walletConnectionConfig'
import Decimal from 'decimal.js'

Decimal.set({
  toExpNeg: -20,
  toExpPos: 20
})

const SWAP_LIMIT = 1000 // Limit for SECH swap amount
const sellTokenRegExp = new RegExp(`^\\d+\\.\\d{${SWAP_TOKEN_DECIMALS + 1},}$`)
const sechTokenRegExp = new RegExp(`^\\d+\\.\\d{${SECH_TOKEN_DECIMAL + 1},}$`)

const sendFormSchema = z.object({
  sellTokenAmount: z.string().refine((val) => !sellTokenRegExp.test(val), {
    message:
      'Input value can contain only up to 18 digits after the decimal point'
  }),
  buyTokenAmount: z
    .string()
    .refine((val) => parseFloat(val) <= SWAP_LIMIT, {
      message:
        'You can swap only 1000 SECH at a time. Please enter a smaller amount'
    })
    .refine((val) => !sechTokenRegExp.test(val), {
      message:
        'Input value can contain only up to 18 digits after the decimal point'
    })
})

const DECIMALS = IS_TESTNET ? 8 : 4

interface SwapProps {
  onSwap: (sellTokenAmount: string, buyTokenAmount: string) => Promise<void>
  isConnectedWallet?: boolean
  sellTokenBalance?: number
  isBalanceLoading?: boolean
}

export default function SwapForm({
  onSwap,
  isConnectedWallet,
  sellTokenBalance,
  isBalanceLoading
}: SwapProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [debounceTimeout, setDebounceTimeout] = useState<NodeJS.Timeout | null>(
    null
  )
  const [rawAmounts, setRawAmounts] = useState({
    sellTokenAmount: '',
    buyTokenAmount: ''
  })
  const form = useForm<z.infer<typeof sendFormSchema>>({
    resolver: zodResolver(sendFormSchema),
    defaultValues: { buyTokenAmount: '', sellTokenAmount: '' }
  })

  const buyTokenAmount = form.watch('buyTokenAmount')
  const sellTokenAmount = form.watch('sellTokenAmount')

  const { tokenPrice, tokenPriceLoading, finalizePriceQuote } =
    useGetTokenPrice()

  const handleSellTokenAmountChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const inputValue = e.target.value

    if (sellTokenRegExp.test(inputValue)) {
      return // Prevent execution if more than 18 decimals
    }

    form.setValue('sellTokenAmount', inputValue)

    if (debounceTimeout) clearTimeout(debounceTimeout)

    if (!parseFloat(inputValue)) {
      form.setValue('buyTokenAmount', '')
      return
    }

    const buyAmount = new Decimal(inputValue)
      .div(tokenPrice)
      .toDecimalPlaces(DECIMALS)
      .toString()
    tokenPrice &&
      parseFloat(inputValue) &&
      form.setValue('buyTokenAmount', buyAmount)

    setDebounceTimeout(
      setTimeout(() => {
        finalizePriceQuote({ sellTokenAmount: inputValue }).then((result) => {
          if (!result?.buyTokenAmount) {
            console.error('Error fetching token price ', result)
          }
          setRawAmounts({
            sellTokenAmount: inputValue,
            buyTokenAmount: result?.buyTokenAmount || ''
          })
          form.setValue(
            'buyTokenAmount',
            new Decimal(result?.buyTokenAmount)
              .toDecimalPlaces(DECIMALS, Decimal.ROUND_DOWN)
              .toString()
          )
        })
      }, 400)
    )
  }
  const handleBuyTokenAmountChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const inputValue = e.target.value

    if (sechTokenRegExp.test(inputValue)) {
      return // Prevent execution if more than 18 decimals
    }

    form.setValue('buyTokenAmount', inputValue)

    if (debounceTimeout) clearTimeout(debounceTimeout)

    if (!parseFloat(inputValue)) {
      form.setValue('sellTokenAmount', '')
      return
    }

    const sellAmount = new Decimal(inputValue)
      .mul(tokenPrice)
      .toDecimalPlaces(DECIMALS, Decimal.ROUND_UP)
      .toString()

    tokenPrice &&
      parseFloat(inputValue) &&
      form.setValue('sellTokenAmount', sellAmount)

    setDebounceTimeout(
      setTimeout(() => {
        finalizePriceQuote({ buyTokenAmount: inputValue }).then((result) => {
          if (!result?.sellTokenAmount) {
            console.error('Error fetching token price ', result)
          }
          setRawAmounts({
            sellTokenAmount: result?.sellTokenAmount || '',
            buyTokenAmount: inputValue
          })

          form.setValue(
            'sellTokenAmount',
            new Decimal(result?.sellTokenAmount)
              .toDecimalPlaces(DECIMALS, Decimal.ROUND_UP)
              .toString()
          )
        })
      }, 400)
    )
  }

  const onSubmit = async (values: z.infer<typeof sendFormSchema>) => {
    setIsLoading(true)

    await onSwap(rawAmounts?.sellTokenAmount, rawAmounts?.buyTokenAmount)
    setIsLoading(false)
    form.reset()
  }

  const hasInsufficientBalance =
    !sellTokenBalance || sellTokenBalance < Number(sellTokenAmount)

  const computedButtonText = isLoading ? (
    <>
      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
      Processing swap...
    </>
  ) : tokenPriceLoading ? (
    <>
      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
      Finalizing price quote...
    </>
  ) : buyTokenAmount ? (
    hasInsufficientBalance ? (
      'Insufficient funds'
    ) : (
      'Swap tokens'
    )
  ) : (
    'Enter amount'
  )

  const sechRate = new Decimal(tokenPrice)
    .toDecimalPlaces(DECIMALS, Decimal.ROUND_UP)
    .toString()

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="sellTokenAmount"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Amount ({SELL_TOKEN_ABBR})</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  step="any"
                  placeholder={`Amount of ${SELL_TOKEN_ABBR} to swap...`}
                  {...field}
                  onChange={handleSellTokenAmountChange}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="buyTokenAmount"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Amount (SECH)</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  step="any"
                  placeholder="Amount of SECH to receive..."
                  {...field}
                  onChange={handleBuyTokenAmountChange}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button
          type="submit"
          disabled={
            !isConnectedWallet ||
            hasInsufficientBalance ||
            isLoading ||
            tokenPriceLoading ||
            !buyTokenAmount
          }
          className="w-full">
          {computedButtonText}
        </Button>
        {sechRate && (
          <div className="mt-2 space-y-2 text-sm text-muted-foreground">
            <div>
              1 SECH = {sechRate} {SELL_TOKEN_ABBR}
            </div>
          </div>
        )}
      </form>
    </Form>
  )
}
